import ApplicableIcon from '@/assets/applicable.svg?react';
import euLogo from '@/assets/eu-logo.png';
import fdaLogo from '@/assets/fda-logo.png';
import isoLogo from '@/assets/iso-logo.png';
import NotApplicableIcon from '@/assets/not_applicable.svg?react';
import { AssigneesBadge } from '@/components/ui/assignees-badge';
import { Button } from '@/components/ui/button';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useGenerateImpactService,
  useProfileService,
  useUpdateContextAssigneesService,
  useUpdateContextStatusService,
} from '@/services/regulatoryWatch';
import { MultiGeographyBadge } from '@/shared/geography/MultiGeographyBadge';
import { MarkdownText } from '@/shared/MarkdownText';
import { cn } from '@/utils/classUtils';
import {
  ApplicabilityStatus,
  GetUpdateDetailResponse,
  ImplementationStatus,
} from '@ditto/shared';
import {
  Archive,
  Ban,
  Check,
  ChevronDown,
  CircleCheck,
  CircleDashed,
  CircleX,
  Compass,
  Copy,
  Download,
  Gem,
  Info,
  Loader2,
  Rss,
  Shield,
  Sparkles,
  Star,
  Stars,
} from 'lucide-react';
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  PRIORITY_LABELS,
  SOURCE_TYPE_LABELS,
  UPDATE_TYPE_LABELS,
} from '../../labels';
import { PriorityIcon } from '../PriorityIcon';
import { UpdateSummaryMarkdown } from './UpdateSummaryMarkdown';

// Source logos mapping - keys are source IDs
const SOURCE_LOGOS: Record<string, string> = {
  fda_guidance: fdaLogo,
  fda_21_cfr: fdaLogo,
  eu_mdr: euLogo,
  eu_ivdr: euLogo,
  mdcg: euLogo,
};

const getSourceLogo = (
  sourceId: string,
  referenceType: string | null
): string | undefined => {
  if (referenceType === 'standard') return isoLogo;
  return SOURCE_LOGOS[sourceId];
};

const getSourceDisplayTitle = (
  sourceTitle: string,
  referenceType: string | null
): string => {
  return referenceType === 'standard' ? 'ISO' : sourceTitle;
};

// Update type badge with correct styling from Figma
export const UpdateTypeBadge = ({ updateType }: { updateType: string }) => {
  const label = UPDATE_TYPE_LABELS[updateType] ?? updateType;

  const baseClasses =
    'inline-flex items-center gap-1.5 h-6 px-2 rounded-lg text-xs font-medium text-neutral-900 tracking-[0.18px]';

  switch (updateType) {
    case 'new':
      return (
        <span className={cn(baseClasses, 'bg-[#DCFCE7]')}>
          <Star className="h-3 w-3" />
          {label}
        </span>
      );
    case 'revision':
      return (
        <span className={cn(baseClasses, 'bg-[#FEF9C3]')}>
          <Copy className="h-3 w-3" />
          {label}
        </span>
      );
    case 'withdrawal':
      return (
        <span className={cn(baseClasses, 'bg-[#FEE2E2]')}>
          <Ban className="h-3 w-3" />
          {label}
        </span>
      );
    case 'status_change':
      return (
        <span className={cn(baseClasses, 'bg-[#DBEAFE]')}>
          <span className="font-bold">&raquo;</span>
          {label}
        </span>
      );
    default:
      return <span className={cn(baseClasses, 'bg-[#F3E8FF]')}>{label}</span>;
  }
};

// Badge component for right panel header
export const InfoBadge: FC<{
  icon?: string;
  label: string;
}> = ({ icon, label }) => (
  <span className="inline-flex items-center gap-[6px] px-[8px] py-[3px] rounded-[8px] text-[12px] font-medium text-[#171717] tracking-[0.18px] bg-[#FAFAFA] border border-solid border-[#E5E5E5] whitespace-nowrap max-w-[200px]">
    {icon && (
      <img
        src={icon}
        alt=""
        className="w-[16px] h-[16px] object-contain shrink-0"
      />
    )}
    <span className="truncate">{label}</span>
  </span>
);

// Document type badge config (matches Settings subscriptions page)
const DOCUMENT_TYPE_BADGE: Record<
  string,
  { icon: FC<{ className?: string }>; bgClass: string }
> = {
  regulation: { icon: Shield, bgClass: 'bg-magenta-100' },
  guidance: { icon: Compass, bgClass: 'bg-purple-100' },
  standard: { icon: Gem, bgClass: 'bg-orange-100' },
  news: { icon: Rss, bgClass: 'bg-blue-100' },
};

const DocumentTypeBadge: FC<{ type: string }> = ({ type }) => {
  const config = DOCUMENT_TYPE_BADGE[type];
  const label = SOURCE_TYPE_LABELS[type] ?? type;
  const TypeIcon = config?.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 h-6 px-2 rounded-lg text-xs font-medium text-neutral-900 tracking-[0.18px] ${config?.bgClass ?? 'bg-neutral-100'}`}
    >
      {TypeIcon && <TypeIcon className="h-3 w-3" />}
      {label}
    </span>
  );
};

/** Paragraph mini/regular + neutral-600 (DITTO-1267), inline with section titles */
const AI_SECTION_LABEL_TEXT =
  'text-xs font-normal leading-[1.6] tracking-[0.015em] text-neutral-600';

const AiGeneratedSectionLabel: FC = () => (
  <span
    className="inline-flex items-center gap-1.5 shrink-0"
    aria-label="AI-generated content"
  >
    <Stars className="h-3.5 w-3.5 text-magenta-500 shrink-0" aria-hidden />
    <span className={AI_SECTION_LABEL_TEXT}>Generated for you</span>
  </span>
);

/** Collapses height and fades when `visible` is false; keeps children mounted. */
const ApplicableOnlyBlock: FC<{
  visible: boolean;
  children: ReactNode;
}> = ({ visible, children }) => (
  <div
    aria-hidden={!visible}
    className={cn(
      'grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none',
      visible ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
    )}
  >
    <div className="min-h-0 overflow-hidden">
      <div
        className={cn(
          'flex flex-col gap-4 transition-opacity duration-200 ease-out motion-reduce:transition-none',
          visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
      >
        {children}
      </div>
    </div>
  </div>
);

const IMPLEMENTATION_LABELS: Record<ImplementationStatus, string> = {
  no: 'No',
  in_progress: 'In progress',
  yes: 'Yes',
};

// Document badges for right panel header
export const DocumentBadges: FC<{ detail: GetUpdateDetailResponse }> = ({
  detail,
}) => {
  const source = detail.reference?.source ?? detail.source;
  if (!source) return null;

  const documentType =
    detail.kind === 'news' ? 'news' : (detail.reference?.type ?? null);

  return (
    <FlexRow gap="2" alignItems="center" className="flex-wrap">
      {documentType && <DocumentTypeBadge type={documentType} />}
      <InfoBadge
        icon={getSourceLogo(source.id, documentType)}
        label={getSourceDisplayTitle(source.title, documentType)}
      />
      {(() => {
        const source = detail.reference?.source ?? detail.source;
        if (!source) return null;
        return (
          <MultiGeographyBadge
            geography={source.geography}
            institutionGeographies={source.institutionGeographies}
            institutionName={source.institutionName}
          />
        );
      })()}
    </FlexRow>
  );
};

// Badge labels for detail page (Figma shows "In review" not "To review")
const STATUS_BADGE_LABELS: Record<string, string> = {
  to_review: 'In review',
  applicable: 'Applicable',
  not_applicable: 'Not applicable',
  out_of_scope: 'Out of scope',
};

const STATUS_BADGE_ICONS: Record<string, FC<{ className?: string }>> = {
  to_review: CircleDashed,
  applicable: CircleCheck,
  not_applicable: CircleX,
  out_of_scope: Archive,
};

// Inline badge matching Figma spec: bg-neutral-50, border-neutral-200, rounded-lg
const InlineBadge: FC<{
  icon: React.ReactNode;
  label: string;
}> = ({ icon, label }) => (
  <span className="inline-flex items-center gap-[6px] px-[8px] py-[3px] rounded-[8px] text-[12px] font-medium text-[#171717] tracking-[0.18px] bg-[#FAFAFA] border border-[#E5E5E5]">
    {icon}
    {label}
  </span>
);

const FILLED_ICONS: Record<string, FC<{ className?: string }>> = {
  applicable: ApplicableIcon,
  not_applicable: NotApplicableIcon,
};

const RECOMMENDATION_BG: Record<string, string> = {
  applicable: 'bg-green-50 hover:bg-green-100',
  not_applicable: 'bg-red-50 hover:bg-red-100',
};

const STATUS_LABELS_ASSESSMENT: Record<string, string> = {
  applicable: 'Applicable',
  not_applicable: 'Not applicable',
  out_of_scope: 'Out of scope',
};

const ASSESSMENT_OPTIONS: {
  value: ApplicabilityStatus;
  icon: FC<{ className?: string }>;
}[] = [
  { value: 'to_review', icon: CircleDashed },
  { value: 'applicable', icon: CircleCheck },
  { value: 'not_applicable', icon: CircleX },
  { value: 'out_of_scope', icon: Archive },
];

const AssessmentSection: FC<{
  currentStatus: ApplicabilityStatus;
  currentJustification: string | null;
  justificationSaveState: 'idle' | 'saving' | 'saved';
  recommendedApplicability: ApplicabilityStatus | null;
  onStatusChange: (status: ApplicabilityStatus) => void;
  onJustificationChange: (justification: string) => void;
}> = ({
  currentStatus,
  currentJustification,
  justificationSaveState,
  recommendedApplicability,
  onStatusChange,
  onJustificationChange,
}) => {
  const [justification, setJustification] = useState(
    currentJustification ?? ''
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    setJustification(currentJustification ?? '');
  }, [currentJustification]);

  const handleJustificationChange = useCallback(
    (value: string) => {
      setJustification(value);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const trimmed = value.trim();
        if (trimmed !== (currentJustification ?? '')) {
          onJustificationChange(trimmed);
        }
      }, 1500);
    },
    [currentJustification, onJustificationChange]
  );

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const isInReview = currentStatus === 'to_review';
  const showJustificationField =
    currentStatus === 'applicable' || currentStatus === 'not_applicable';

  return (
    <FlexCol gap="3">
      <FlexRow alignItems="center" gap="1.5">
        <span className="text-[11px] uppercase tracking-[1px] font-medium text-neutral-500">
          Assessment
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="h-3.5 w-3.5 text-neutral-400" />
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="max-w-[400px] text-sm leading-relaxed"
          >
            <p>
              <strong>To review</strong> — This regulatory update has not been
              assessed. Review to determine if it applies to your device(s).
            </p>
            <p className="mt-2">
              <strong>Applicable</strong> — This regulatory update has been
              assessed and determined to impact your device(s). Actions may be
              required to maintain regulatory compliance.
            </p>
            <p className="mt-2">
              <strong>Not applicable</strong> — This regulatory update has been
              assessed and determined not to impact your device(s).
            </p>
            <p className="mt-2">
              <strong>Out of scope</strong> — This regulatory update falls
              outside the scope relevant to your device(s), such as different
              jurisdictions or device categories.
            </p>
          </TooltipContent>
        </Tooltip>
      </FlexRow>

      <div className="flex gap-2">
        {ASSESSMENT_OPTIONS.map(opt => {
          const isActive = currentStatus === opt.value;
          const isRecommended =
            isInReview &&
            recommendedApplicability === opt.value &&
            (opt.value === 'applicable' || opt.value === 'not_applicable');
          const FilledIcon = FILLED_ICONS[opt.value];
          const Icon = opt.icon;
          const label = STATUS_LABELS_ASSESSMENT[opt.value];

          // Recommended state: colored bg, black border, flex-1, label + "Recommended"
          if (isRecommended && FilledIcon) {
            return (
              <button
                key={opt.value}
                className={cn(
                  'flex items-center justify-center gap-2 flex-1 min-w-0 h-[40px] px-4 rounded-lg border border-black shadow-sm transition-colors cursor-pointer',
                  RECOMMENDATION_BG[opt.value]
                )}
                onClick={() => onStatusChange(opt.value)}
              >
                <FilledIcon className="h-[15px] w-[15px] shrink-0" />
                <div className="flex items-baseline gap-1 min-w-0">
                  <span className="text-sm font-medium text-neutral-900 tracking-[0.07px] whitespace-nowrap">
                    {label}
                  </span>
                  <span className="text-xs italic text-neutral-500 whitespace-nowrap">
                    Recommended
                  </span>
                </div>
              </button>
            );
          }

          // Active (manually classified): neutral bg, flex-1, label
          if (isActive && opt.value !== 'to_review') {
            return (
              <button
                key={opt.value}
                className="flex items-center justify-center gap-2 flex-1 min-w-0 h-[40px] px-4 rounded-lg bg-neutral-100 border border-neutral-200 transition-colors cursor-pointer"
                onClick={() => onStatusChange(opt.value)}
              >
                {FilledIcon ? (
                  <FilledIcon className="h-[15px] w-[15px] shrink-0" />
                ) : (
                  <Icon className="h-[13px] w-[13px] shrink-0 text-neutral-900" />
                )}
                <span className="text-sm font-medium text-neutral-900 tracking-[0.07px] whitespace-nowrap">
                  {label}
                </span>
              </button>
            );
          }

          // to_review button: icon-only, different bg based on whether it's active
          if (opt.value === 'to_review') {
            return (
              <button
                key={opt.value}
                className={cn(
                  'flex items-center justify-center shrink-0 h-[40px] min-w-[40px] rounded-lg border transition-colors cursor-pointer',
                  isActive
                    ? 'bg-neutral-100 border-neutral-200'
                    : 'bg-white border-neutral-200 shadow-sm hover:bg-neutral-50'
                )}
                onClick={() => onStatusChange(opt.value)}
                title="In review"
              >
                <CircleDashed className="h-[13px] w-[13px] text-neutral-400" />
              </button>
            );
          }

          // Default: icon-only button, self-stretch
          return (
            <button
              key={opt.value}
              className="flex items-center justify-center self-stretch shrink-0 min-w-[40px] rounded-lg bg-white border border-neutral-200 shadow-sm hover:bg-neutral-50 transition-colors cursor-pointer"
              onClick={() => onStatusChange(opt.value)}
              title={label}
            >
              {FilledIcon ? (
                <FilledIcon className="h-[15px] w-[15px]" />
              ) : (
                <Icon className="h-[13px] w-[13px] text-neutral-900" />
              )}
            </button>
          );
        })}
      </div>

      <div
        aria-hidden={!showJustificationField}
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none',
          showJustificationField ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <FlexCol
            gap="1.5"
            className={cn(
              'transition-opacity duration-200 ease-out motion-reduce:transition-none',
              showJustificationField
                ? 'opacity-100'
                : 'opacity-0 pointer-events-none'
            )}
          >
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-neutral-700">
                Justification
              </label>
              {justificationSaveState === 'saving' && (
                <span className="text-[11px] text-neutral-400">Saving...</span>
              )}
              {justificationSaveState === 'saved' && (
                <span className="flex items-center gap-0.5 text-[11px] text-neutral-400">
                  <Check className="h-3 w-3" />
                  Saved
                </span>
              )}
            </div>
            <Textarea
              value={justification}
              onChange={e => handleJustificationChange(e.target.value)}
              placeholder="Explain the rationale behind this assessment"
              className="h-[58px] min-h-[58px] text-sm resize-none overflow-y-auto"
              tabIndex={showJustificationField ? undefined : -1}
            />
          </FlexCol>
        </div>
      </div>
    </FlexCol>
  );
};

// Format date for right panel header
export const FormattedDateShort: FC<{
  dateString: string | null | undefined;
}> = ({ dateString }) => {
  if (!dateString) return <span className="text-neutral-400">-</span>;
  const date = new Date(dateString);
  return (
    <span className="text-sm font-medium text-neutral-900">
      {date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })}
    </span>
  );
};

// Left info panel — main export
type LeftInfoPanelProps = {
  detail: GetUpdateDetailResponse;
  /** Hide the status/priority/assignee badges and title (when shown elsewhere) */
  hideBadgesAndTitle?: boolean;
};

export const LeftInfoPanel: FC<LeftInfoPanelProps> = ({
  detail,
  hideBadgesAndTitle = false,
}) => {
  const {
    updateStatus,
    updateActionPlan,
    updateSupportingEvidence,
    updateImplementationStatus,
  } = useUpdateContextStatusService();
  const { updateAssignees } = useUpdateContextAssigneesService();
  const { profile } = useProfileService();
  const { generateImpact, isGenerating } = useGenerateImpactService();

  const [justificationSaveState, setJustificationSaveState] = useState<
    'idle' | 'saving' | 'saved'
  >('idle');
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const [actionPlanDraft, setActionPlanDraft] = useState('');
  const actionPlanDebounceRef =
    useRef<ReturnType<typeof setTimeout>>(undefined);
  const [supportingEvidenceDraft, setSupportingEvidenceDraft] = useState('');
  const supportingEvidenceDebounceRef =
    useRef<ReturnType<typeof setTimeout>>(undefined);

  const [consultantCollapsed, setConsultantCollapsed] = useState(false);

  const hasProfile = !!profile?.products?.length;
  const hasUpdateSummary = !!detail.update.summary;
  const hasImpactSummary = !!detail.context?.impactSummary;
  const hasSuggestedActions = !!detail.context?.suggestedActions;
  const canGenerateImpact = hasProfile && hasUpdateSummary;
  const isImpactGenerating =
    isGenerating || detail.context?.aiImpactStatus === 'generating';

  const serverStatus = (detail.context?.status ??
    'to_review') as ApplicabilityStatus;
  const [optimisticStatus, setOptimisticStatus] =
    useState<ApplicabilityStatus | null>(null);
  const currentStatus = optimisticStatus ?? serverStatus;
  const showApplicableOnlyFields = currentStatus === 'applicable';
  const currentJustification = detail.context?.statusJustification ?? null;

  useEffect(() => {
    setActionPlanDraft(detail.context?.actionPlan ?? '');
  }, [detail.context?.actionPlan]);

  useEffect(() => {
    setSupportingEvidenceDraft(detail.context?.supportingEvidence ?? '');
  }, [detail.context?.supportingEvidence]);

  const handleActionPlanChange = useCallback(
    (value: string) => {
      setActionPlanDraft(value);
      const ctx = detail.context;
      if (!ctx) return;
      clearTimeout(actionPlanDebounceRef.current);
      actionPlanDebounceRef.current = setTimeout(() => {
        const trimmed = value.trim();
        if (trimmed !== (ctx.actionPlan ?? '')) {
          updateActionPlan(ctx.id, trimmed);
        }
      }, 1500);
    },
    [detail.context, updateActionPlan]
  );

  const handleSupportingEvidenceChange = useCallback(
    (value: string) => {
      setSupportingEvidenceDraft(value);
      const ctx = detail.context;
      if (!ctx) return;
      clearTimeout(supportingEvidenceDebounceRef.current);
      supportingEvidenceDebounceRef.current = setTimeout(() => {
        const trimmed = value.trim();
        if (trimmed !== (ctx.supportingEvidence ?? '')) {
          updateSupportingEvidence(ctx.id, trimmed);
        }
      }, 1500);
    },
    [detail.context, updateSupportingEvidence]
  );

  useEffect(() => {
    return () => {
      clearTimeout(actionPlanDebounceRef.current);
      clearTimeout(supportingEvidenceDebounceRef.current);
    };
  }, []);

  const handleImplementationChange = (value: string) => {
    if (!detail.context) return;
    const next: ImplementationStatus | null =
      value === 'no' ? null : (value as ImplementationStatus);
    updateImplementationStatus(detail.context.id, next);
  };

  // Clear optimistic state once server catches up
  useEffect(() => {
    if (optimisticStatus && serverStatus === optimisticStatus) {
      setOptimisticStatus(null);
    }
  }, [serverStatus, optimisticStatus]);

  const getDisabledReason = () => {
    if (!hasProfile) {
      return 'Please set up your company profile with device descriptions first';
    }
    if (!hasUpdateSummary) {
      return 'This update has no summary to assess';
    }
    return null;
  };

  const handleStatusChange = (status: ApplicabilityStatus) => {
    if (detail.context) {
      setOptimisticStatus(status);
      updateStatus(detail.context.id, status);
    }
  };

  const handleJustificationChange = (justification: string) => {
    if (detail.context) {
      setJustificationSaveState('saving');
      clearTimeout(savedTimerRef.current);
      updateStatus(detail.context.id, undefined, justification, {
        onSuccess: () => {
          setJustificationSaveState('saved');
          savedTimerRef.current = setTimeout(
            () => setJustificationSaveState('idle'),
            2000
          );
        },
        onError: () => {
          setJustificationSaveState('idle');
        },
      });
    }
  };

  useEffect(() => {
    return () => clearTimeout(savedTimerRef.current);
  }, []);

  const assessmentContent = (
    <FlexCol gap="4">
      <AssessmentSection
        currentStatus={currentStatus}
        currentJustification={currentJustification}
        justificationSaveState={justificationSaveState}
        recommendedApplicability={
          detail.context?.recommendedApplicability ?? null
        }
        onStatusChange={handleStatusChange}
        onJustificationChange={handleJustificationChange}
      />

      <ApplicableOnlyBlock visible={showApplicableOnlyFields}>
        <FlexCol gap="4">
          <FlexCol gap="1.5">
            <label className="text-sm font-medium text-neutral-700">
              Action plan
            </label>
            <Textarea
              value={actionPlanDraft}
              onChange={e => handleActionPlanChange(e.target.value)}
              placeholder="Describe planned actions for this update"
              className="h-[58px] min-h-[58px] text-sm resize-none overflow-y-auto"
              tabIndex={showApplicableOnlyFields ? undefined : -1}
            />
          </FlexCol>

          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-neutral-700 shrink-0">
              Implemented
            </span>
            <Select
              value={
                detail.context?.implementationStatus === null ||
                detail.context?.implementationStatus === undefined
                  ? 'no'
                  : detail.context.implementationStatus
              }
              onValueChange={handleImplementationChange}
              disabled={!detail.context}
            >
              <SelectTrigger
                className="h-9 w-auto min-w-[140px] flex-1 max-w-[280px]"
                tabIndex={showApplicableOnlyFields ? undefined : -1}
              >
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no">{IMPLEMENTATION_LABELS.no}</SelectItem>
                <SelectItem value="in_progress">
                  {IMPLEMENTATION_LABELS.in_progress}
                </SelectItem>
                <SelectItem value="yes">{IMPLEMENTATION_LABELS.yes}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <FlexCol gap="1.5">
            <label className="text-sm font-medium text-neutral-700">
              Supporting evidence
            </label>
            <Textarea
              value={supportingEvidenceDraft}
              onChange={e => handleSupportingEvidenceChange(e.target.value)}
              placeholder="Links or notes for evidence supporting your plan"
              className="h-[58px] min-h-[58px] text-sm resize-none overflow-y-auto"
              tabIndex={showApplicableOnlyFields ? undefined : -1}
            />
          </FlexCol>
        </FlexCol>
      </ApplicableOnlyBlock>
    </FlexCol>
  );

  // Content sections shared by both layouts
  const contentSections = (
    <>
      {/* Status + Priority badges + Title */}
      {!hideBadgesAndTitle && (
        <div className="flex flex-col gap-2">
          <FlexRow gap="2" alignItems="center">
            {(() => {
              const FilledIcon = FILLED_ICONS[currentStatus];
              if (FilledIcon) {
                return (
                  <InlineBadge
                    icon={<FilledIcon className="h-3.5 w-3.5" />}
                    label={STATUS_BADGE_LABELS[currentStatus] ?? currentStatus}
                  />
                );
              }
              const StatusIcon =
                STATUS_BADGE_ICONS[currentStatus] ?? CircleDashed;
              return (
                <InlineBadge
                  icon={<StatusIcon className="h-3.5 w-3.5 text-neutral-400" />}
                  label={STATUS_BADGE_LABELS[currentStatus] ?? currentStatus}
                />
              );
            })()}
            {detail.context?.priority && detail.context.priority !== 'none' && (
              <InlineBadge
                icon={
                  <PriorityIcon
                    priority={detail.context.priority}
                    className="h-3.5 w-3.5 text-neutral-900"
                  />
                }
                label={
                  PRIORITY_LABELS[detail.context.priority] ??
                  detail.context.priority
                }
              />
            )}
            {detail.context && (
              <AssigneesBadge
                assignedMemberIds={detail.context.assignedMemberIds ?? []}
                onAssign={memberIds =>
                  updateAssignees({
                    id: detail.context!.id,
                    memberIds,
                  })
                }
              />
            )}
          </FlexRow>
          <p className="text-[16px] font-medium text-neutral-900 leading-[1.4]">
            {detail.update.title ||
              detail.update.shortSummary ||
              detail.reference?.title ||
              'News Update'}
          </p>
        </div>
      )}

      {/* Consultant analysis */}
      {detail.consultantAnalysis && (
        <>
          {!hideBadgesAndTitle && (
            <div className="py-4">
              <div className="h-px w-full bg-neutral-200" />
            </div>
          )}
          <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4 flex flex-col gap-2">
            <FlexRow alignItems="center" gap="2" className="justify-between">
              <FlexRow alignItems="center" gap="2">
                {detail.consultantAnalysis.orgLogo && (
                  <img
                    src={`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}${detail.consultantAnalysis.orgLogo}`}
                    alt=""
                    className="h-10 w-10 rounded object-contain"
                  />
                )}
                <p className="text-sm font-medium text-blue-900">
                  {detail.consultantAnalysis.orgName} Analysis
                </p>
              </FlexRow>
              <FlexRow alignItems="center" gap="2">
                {!consultantCollapsed && detail.consultantAnalysis.file && (
                  <a
                    href={detail.consultantAnalysis.file.url}
                    download={detail.consultantAnalysis.file.name}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 transition-colors shrink-0"
                  >
                    <Download className="size-3.5" />
                    Download detailed analysis
                  </a>
                )}
                <button
                  onClick={() => setConsultantCollapsed(prev => !prev)}
                  className="text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <ChevronDown
                    className={cn(
                      'size-4 transition-transform duration-200',
                      consultantCollapsed && '-rotate-90'
                    )}
                  />
                </button>
              </FlexRow>
            </FlexRow>
            {!consultantCollapsed && (
              <>
                {detail.consultantAnalysis.shortSummary && (
                  <p className="text-sm text-blue-800 font-medium">
                    {detail.consultantAnalysis.shortSummary}
                  </p>
                )}
                {detail.consultantAnalysis.summary && (
                  <MarkdownText size="sm" className="text-blue-900">
                    {detail.consultantAnalysis.summary}
                  </MarkdownText>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Separator (skip when badges/title are hidden and no consultant analysis above) */}
      {(!hideBadgesAndTitle || detail.consultantAnalysis) && (
        <div className="py-4">
          <div className="h-px w-full bg-neutral-200" />
        </div>
      )}

      {/* Update summary */}
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium leading-[1.6] text-black tracking-[0.07px]">
          Update summary
        </p>
        {detail.update.summary ? (
          <UpdateSummaryMarkdown markdown={detail.update.summary} />
        ) : (
          <p className="text-sm font-normal leading-[1.44] tracking-[0.005em] text-neutral-400 italic">
            No summary available
          </p>
        )}
      </div>

      <div className="py-4">
        <div className="h-px w-full bg-neutral-200" />
      </div>

      {/* Impact assessment */}
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className="text-sm font-medium leading-[1.6] text-black tracking-[0.07px]">
            Impact assessment
          </p>
          <AiGeneratedSectionLabel />
        </div>

        {hasImpactSummary && !isImpactGenerating ? (
          <MarkdownText variant="updateDetail">
            {detail.context?.impactSummary ?? ''}
          </MarkdownText>
        ) : isImpactGenerating ? (
          <FlexRow gap="2" alignItems="center">
            <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />
            <Text className="text-sm text-neutral-500">
              Generating assessment...
            </Text>
          </FlexRow>
        ) : canGenerateImpact ? (
          <FlexCol gap="2">
            <Text className="text-sm text-neutral-500">
              Generate a personalized impact assessment based on your company
              profile.
            </Text>
            <Button
              variant="outline"
              size="sm"
              className="w-fit"
              onClick={() =>
                detail.context && generateImpact(detail.context.id, detail.id)
              }
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Generate Assessment
            </Button>
          </FlexCol>
        ) : (
          <FlexCol gap="2">
            <Text className="text-sm text-neutral-500">
              Generate a personalized impact assessment based on your company
              profile.
            </Text>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="w-fit">
                  <Button variant="outline" size="sm" disabled>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Assessment
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{getDisabledReason()}</TooltipContent>
            </Tooltip>
          </FlexCol>
        )}
      </div>

      {/* Recommendations */}
      {hasSuggestedActions && (
        <>
          <div className="py-4">
            <div className="h-px w-full bg-neutral-200" />
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <p className="text-sm font-medium leading-[1.6] text-black tracking-[0.07px]">
                Recommendations
              </p>
              <AiGeneratedSectionLabel />
            </div>
            <MarkdownText variant="updateDetail">
              {detail.context?.suggestedActions ?? ''}
            </MarkdownText>
          </div>
        </>
      )}
    </>
  );

  if (hideBadgesAndTitle) {
    return (
      <div className="flex h-full">
        <div className="w-1/2 overflow-y-auto">
          {/* pl-12 (48px): align with Overview tab label (header px-6 + tab px-6) */}
          <div className="flex flex-col gap-4 pl-12 pr-6 py-6 pb-12 max-w-[600px]">
            {contentSections}
          </div>
        </div>
        <div className="w-1/2 overflow-y-auto">
          <div className="px-6 py-6 pb-12 max-w-[600px]">
            {assessmentContent}
          </div>
        </div>
      </div>
    );
  }

  return (
    <FlexCol className="h-full">
      <div className="flex-1 overflow-y-auto border-b border-neutral-200">
        <div className="flex flex-col gap-4 px-6 py-6">{contentSections}</div>
      </div>
      <div className="px-6 py-5 shrink-0">{assessmentContent}</div>
    </FlexCol>
  );
};
